import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, Share, Smartphone } from "lucide-react";
import { logDev } from "@/lib/logger";

/**
 * PWA Install Prompt — Xenia.
 *
 * Strategy:
 *  • Android / Desktop Chrome: capture `beforeinstallprompt` and offer a 1-tap
 *    "Installa Xenia" button.
 *  • iOS Safari: no programmatic install API exists, so we show short
 *    manual instructions ("Condividi → Aggiungi alla schermata Home").
 *  • Dismissed prompt is remembered for 14 days via localStorage.
 *  • Already-installed apps (display-mode: standalone) never show the banner.
 */
const DISMISS_KEY = "xenia_pwa_install_dismissed_at";
const DISMISS_TTL_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_TTL_DAYS;
  } catch (err) { logDev("pwa wasRecentlyDismissed", err); return false; }
}

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [iosVisible, setIosVisible] = useState(false);
  const [androidVisible, setAndroidVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // iOS Safari: no native event, show manual hint after 5s delay
    if (isiOS()) {
      const timer = setTimeout(() => setIosVisible(true), 5000);
      return () => clearTimeout(timer);
    }

    // Android / Chrome: listen for the install event
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setAndroidVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { logDev("pwa dismiss", err); }
    setIosVisible(false);
    setAndroidVisible(false);
  };

  const installAndroid = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { logDev("pwa installAndroid persist", err); }
      }
    } catch (err) { logDev("pwa installAndroid userChoice", err); }
    setDeferred(null);
    setAndroidVisible(false);
  };

  if (androidVisible) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60]"
        data-testid="pwa-install-android">
        <div className="bg-white border-2 border-blue-200 shadow-2xl rounded-sm p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-700 text-white rounded-sm flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-stone-900">{t("pwa.android_title")}</p>
              <p className="text-xs text-stone-600 mt-1">{t("pwa.android_body")}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={installAndroid}
                  className="bg-blue-700 text-white px-4 py-2 text-xs rounded-sm hover:bg-blue-700 inline-flex items-center gap-1.5"
                  data-testid="pwa-install-android-cta">
                  <Download className="w-3.5 h-3.5" />
                  {t("pwa.cta_install")}
                </button>
                <button onClick={dismiss} className="text-stone-500 text-xs px-2"
                  data-testid="pwa-install-android-dismiss">{t("pwa.later")}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (iosVisible) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60]"
        data-testid="pwa-install-ios">
        <div className="bg-white border-2 border-blue-200 shadow-2xl rounded-sm p-4">
          <button onClick={dismiss} aria-label="dismiss"
            className="absolute top-2 right-2 p-1 hover:bg-stone-100 rounded-sm"
            data-testid="pwa-install-ios-dismiss">
            <X className="w-3.5 h-3.5 text-stone-500" />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 bg-blue-700 text-white rounded-sm flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-stone-900">{t("pwa.ios_title")}</p>
              <p className="text-xs text-stone-700 mt-2 leading-snug">
                {t("pwa.ios_step1")}<Share className="inline w-3.5 h-3.5 mx-0.5 align-text-bottom" />
              </p>
              <p className="text-xs text-stone-700 mt-1 leading-snug">{t("pwa.ios_step2")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
